/**
 * shell/ViewRing.tsx —— **视图环**（壳规范 §4：`tablist`＝对话／轨迹／其它已注册视图）。
 *
 * 形态（§1bis；A6②）：桌面／平板＝顶部 `tablist`；手机＝**视图选择器**（`mode==="selector"`：
 *   一个位置的真按钮（`aria-haspopup="listbox"`／`aria-expanded`）＋ `role="listbox"` 内
 *   `role="option"` 列表；8 项平铺 390px 装不下 ⇒ 收成一个位置，底部 tab 病灶一并消除）。
 * 键盘完备（A6②）：三形态都走 **roving tabindex**（列表内 Tab 键只停当前项，↑↓/←→ 移动）；
 *   顶层 tab ↑↓/←→ 移动焦点；选择器列表 ↑↓ 移动、Home/End 首尾、Enter/Space 选中、
 *   Esc/Tab 收起（焦点回按钮）。
 * 纪律：条目＝视图族（`views/*.yaml`）**派生**，非写死；选中态＝**色＋字重双编码**（判据
 *      `ui_mobile_touch_feedback`）；每个条目**都是真按钮**且带 `data-reach-view`（可达自证）。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ViewRingMode } from "./shellDecl";
import { ABSENT_DECLARED_NOTE } from "./absentNote";
import type { ViewEntry } from "./types";

export interface ViewRingProps {
  entries: ViewEntry[];
  active: string;
  absent: Set<string>;
  onPick: (name: string) => void;
  mode: ViewRingMode;
  /** tab 条末端控件（§5：右栏形态切换·折叠搭在此） */
  tail?: React.ReactNode;
}

/** roving tabindex 焦点移动：在容器内的条目间循环（↑↓／←→；Home/End） */
function focusSibling(items: HTMLElement[], current: HTMLElement, key: string): void {
  const index = items.indexOf(current);
  if (index < 0) return;
  let next = index;
  if (key === "ArrowDown" || key === "ArrowRight") next = (index + 1) % items.length;
  else if (key === "ArrowUp" || key === "ArrowLeft") next = (index - 1 + items.length) % items.length;
  else if (key === "Home") next = 0;
  else if (key === "End") next = items.length - 1;
  else return;
  items[next]?.focus();
}

export function ViewRing(props: ViewRingProps): React.ReactElement {
  const { mode } = props;
  const listRef = useRef<HTMLDivElement | null>(null);
  // selector：开合态（点开 ⇒ 列表呈现；选视图/Esc/失焦 ⇒ 收起）
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // 断点切换（selector ⇒ top/bottom）⇒ 收起残留开态
    if (mode !== "selector") setOpen(false);
  }, [mode]);

  /** roving：Tab 键在列表内只停「当前项」（选中项优先，否则首个可用项） */
  const currentTabIndex = useCallback(
    (name: string): number => (name === props.active ? 0 : -1),
    [props.active],
  );

  const onKeyDownRoving = useCallback(
    (event: React.KeyboardEvent<HTMLElement>): void => {
      const container = listRef.current;
      if (!container) return;
      const items = Array.from(container.querySelectorAll<HTMLButtonElement>("[role='tab'], [role='option']"))
        .filter((el) => !el.disabled);
      if (!items.includes(document.activeElement as HTMLButtonElement)) return;
      if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        focusSibling(items, document.activeElement as HTMLElement, event.key);
      }
    },
    [],
  );

  /** ── selector（手机档）：当前视图真按钮 ＋ listbox 选项列表 ── */
  if (mode === "selector") {
    const current = props.entries.find((entry) => entry.name === props.active) ?? props.entries[0];
    const closeAndRefocus = (): void => {
      setOpen(false);
      const trigger = rootRef.current?.querySelector<HTMLButtonElement>("[data-testid='view-ring-current']");
      trigger?.focus();
    };
    return (
      <div
        ref={rootRef}
        data-testid="view-ring"
        data-mode={mode}
        className="xg-viewsel"
        onKeyDown={(event) => {
          if (!open) return;
          if (event.key === "Escape") {
            event.stopPropagation();
            closeAndRefocus();
          }
        }}
      >
        <button
          type="button"
          data-testid="view-ring-current"
          data-reach-view={current?.name}
          data-reach-via="viewring"
          className="xg-viewsel-trigger xg-tab"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {current?.title ?? ""}
          <span aria-hidden="true">▾</span>
        </button>
        <div
          ref={listRef}
          role="listbox"
          aria-label="视图"
          data-testid="view-ring-list"
          hidden={!open}
          onKeyDown={onKeyDownRoving}
          className="xg-viewsel-list"
        >
          {props.entries.map((entry) => {
            const absent = props.absent.has(entry.name);
            const selected = entry.name === props.active;
            return (
              <button
                key={entry.name}
                type="button"
                role="option"
                data-testid={"navtab-" + entry.name}
                data-reach-view={entry.name}
                data-reach-via="viewring"
                aria-selected={selected}
                aria-disabled={absent || undefined}
                disabled={absent}
                tabIndex={currentTabIndex(entry.name)}
                title={absent ? ABSENT_DECLARED_NOTE : entry.title}
                onClick={() => {
                  if (absent) return;
                  props.onPick(entry.name);
                  closeAndRefocus();
                }}
                className="xg-tab xg-viewsel-option"
              >{entry.title}</button>
            );
          })}
        </div>
      </div>
    );
  }

  /** ── top／bottom：tablist ＋ roving tabindex ── */
  const tabs = (
    <div
      ref={listRef}
      data-testid="view-ring"
      data-mode={mode}
      role="tablist"
      aria-label="视图"
      className={mode === "bottom" ? "xg-tabbar" : "xg-tabs"}
      onKeyDown={onKeyDownRoving}
    >
      {props.entries.map((entry) => {
        const absent = props.absent.has(entry.name);
        const selected = entry.name === props.active;
        return (
          <button
            key={entry.name}
            type="button"
            role="tab"
            data-testid={(mode === "bottom" ? "navtab-" : "nav-") + entry.name}
            data-reach-view={entry.name}
            data-reach-via="viewring"
            aria-selected={selected}
            aria-disabled={absent || undefined}
            disabled={absent}
            tabIndex={currentTabIndex(entry.name)}
            title={absent ? ABSENT_DECLARED_NOTE : entry.title}
            onClick={() => { if (!absent) props.onPick(entry.name); }}
            className="xg-tab"
          >{entry.title}</button>
        );
      })}
      {props.tail && mode === "top" ? <span className="xg-tabs-tail">{props.tail}</span> : null}
    </div>
  );
  return tabs;
}
